import { FC, useEffect, useRef, useState } from 'react';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import StyledGridContainer, {
  CenteredCellBox,
} from 'src/components/StyledComponents/StyledGridContainer';
import Status from 'src/components/Status';
import CustomNoRowsOverlay from 'src/components/StyledComponents/CustomEmptyTableRow';
import { useGetOrder } from 'src/services/order.service';
import {
  OrderWithCustomer,
  OrderFilterValues,
} from 'src/models/Management.interface';
import LoadingTable from 'src/components/StyledComponents/CustomLoadingTable';
import StyledDataGrid from 'src/components/StyledComponents/StyledDataGrid';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import OrderActionMenuButton from 'src/components/ActionButtons/OrderActionMenu';
import PaginationControl from 'src/components/StyledComponents/StyledPagination';
import { SortBy, SortOrder } from 'src/models/Sort.types';
import { pagination } from 'src/utils/properties.utils';
import { formatDateYYYYMMDD } from 'src/utils/format.utils';

const OrderGrid: FC<{
  state: boolean;
  searchTerm: string;
  filters: OrderFilterValues;
}> = ({ state, searchTerm, filters }) => {
  const { showSnackbar } = StyledSnackbar();
  const [page, setPage] = useState<number>(pagination.DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState<number>(
    pagination.DEFAULT_PAGE_SIZE
  );
  const { loading, orders, totalItems, fetchOrders, deleteOrder } =
    useGetOrder();
  const [sortBy, setSortBy] = useState<SortBy>('orderNumber');
  const [order, setOrder] = useState<SortOrder>(SortOrder.DESC);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'orderNumber', sort: 'desc' },
  ]);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const isFirstRender = useRef(true);

  useEffect(() => {
    fetchOrders(
      page,
      pageSize,
      searchTerm,
      sortBy,
      order,
      filters.status,
      filters.customerId,
      filters.createdStartDate,
      filters.createdEndDate,
      filters.scheduledStartDate,
      filters.scheduledEndDate,
      filters.actualStartDate,
      filters.actualEndDate
    );
  }, [filters, page, pageSize, searchTerm, sortBy, order, refreshKey]);

  // Reset to page 1 whenever a new order is created (state toggles)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (page === pagination.DEFAULT_PAGE) {
      setRefreshKey((prev) => prev + 1);
    } else {
      setPage(pagination.DEFAULT_PAGE);
    }
  }, [state]);

  useEffect(() => {
    setPage(pagination.DEFAULT_PAGE);
  }, [searchTerm, filters]);

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
    const sort = model[0];
    setPage(pagination.DEFAULT_PAGE);
    setSortBy((sort?.field as SortBy) || 'orderNumber');
    setOrder((sort?.sort as SortOrder) || SortOrder.DESC);
  };

  const columns: GridColDef[] = [
    { field: 'orderNumber', headerName: 'Order Number', flex: 0.8 },
    { field: 'name', headerName: 'Customer Name', flex: 1 },
    {
      field: 'createdAt',
      headerName: 'Order Date',
      flex: 0.7,
      renderCell: (params) => formatDateYYYYMMDD(params.row.createdAt),
    },
    {
      field: 'scheduledDeliveryDate',
      headerName: 'Scheduled Delivery',
      flex: 0.8,
      renderCell: (params) =>
        formatDateYYYYMMDD(params.row.scheduledDeliveryDate),
    },
    {
      field: 'actualDeliveryDate',
      headerName: 'Actual Delivery',
      flex: 0.8,
      renderCell: (params) => formatDateYYYYMMDD(params.row.actualDeliveryDate),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.9,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <CenteredCellBox>
          <Status status={params.value} />
        </CenteredCellBox>
      ),
    },
    {
      field: 'grandTotal',
      headerName: 'Grand Total',
      flex: 0.7,
      headerAlign: 'right',
      align: 'right',
      sortable: false,
      valueGetter: (value: number) => {
        return `${Number(value).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      },
    },
    {
      field: 'paidAmount',
      headerName: 'Paid Amount',
      flex: 0.7,
      headerAlign: 'right',
      align: 'right',
      sortable: false,
      valueGetter: (value: number) => {
        return `${Number(value).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      },
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <OrderActionMenuButton
          rowId={String(params.id)}
          orderNumber={params.row.orderNumber}
          status={params.row.status}
          fetchOrders={() =>
            fetchOrders(
              page,
              pageSize,
              searchTerm,
              sortBy,
              order,
              filters.status,
              filters.customerId,
              filters.createdStartDate,
              filters.createdEndDate,
              filters.scheduledStartDate,
              filters.scheduledEndDate,
              filters.actualStartDate,
              filters.actualEndDate
            )
          }
          deleteOrder={async (id: string) => {
            await deleteOrder(id);
            if (page === pagination.DEFAULT_PAGE) {
              setRefreshKey((prev) => prev + 1);
            } else {
              setPage(pagination.DEFAULT_PAGE);
            }
          }}
        />
      ),
    },
  ];

  const transformData = (orders: OrderWithCustomer[]) => {
    try {
      return orders.map((order, index) => ({
        id: order.id,
        number: (page - 1) * pageSize + index + 1,
        orderNumber: order.orderNumber,
        name: order.customerName,
        createdAt: order.orderDate,
        scheduledDeliveryDate: order.scheduledDeliveryDate,
        actualDeliveryDate: order.actualDeliveryDate,
        status: order.status,
        grandTotal: order.grandTotal,
        paidAmount: order.paidAmount,
      }));
    } catch (error) {
      showSnackbar('Error transforming order data', 'error');
      return [];
    }
  };

  const rows = transformData(orders);

  return loading ? (
    <LoadingTable columns={columns} />
  ) : (
    <StyledGridContainer
      pagination={
        totalItems > 0 ? (
          <PaginationControl
            totalItems={totalItems}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            loading={loading}
          />
        ) : undefined
      }
    >
      <StyledDataGrid
        rows={rows}
        columns={columns}
        sortingMode="server"
        sortModel={sortModel}
        sortingOrder={['asc', 'desc']}
        onSortModelChange={handleSortModelChange}
        disableColumnMenu
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
      />
    </StyledGridContainer>
  );
};

export default OrderGrid;
